import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { OfferPageMultiRoomAutoselectionReason } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { EmptyArray } from 'lib/array/arrayUtils'
import { arePropertyRatesLoading, getPropertyRatesKey } from 'selectors/offerPage/offerPageSelectors'
import {
  getActivePropertyRatesByRoom,
  getPropertyIndividualRowsByRoomId,
  getSelectedPropertyRatesByRoom,
} from 'selectors/offerPage/propertyMultipleRoomBookingSelectors'
import { isSppMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

import useOfferPageDispatch from './useOfferPageDispatch'

export default function usePropertyIndividualRoomAutoselection(enabled = true) {
  const offerPageState = useContext(OfferPageStateContext)
  const {
    rooms,
    property,
    multiRoomAutoselectionReason,
  } = offerPageState

  const dispatch = useOfferPageDispatch()
  // Cap 'invalid-selections' reason to first resolve; later cascade resolutions should not re-render the stale-selection banner.
  const hasResolvedOnceRef = useRef(false)

  const isMultiRoomUIActive = useAppSelector(appState =>
    enabled && !!property && isSppMultipleRoomBookingActive(offerPageState, appState),
  )

  const isRatesLoading = useAppSelector(appState => arePropertyRatesLoading(offerPageState, appState))

  // Empty before dates are chosen; used to branch into the no-dates room-only pre-selection.
  const propertyRatesKey = useAppSelector(appState => getPropertyRatesKey(offerPageState, appState))
  const rowsByRoomId = useAppSelector(appState => getPropertyIndividualRowsByRoomId(offerPageState, appState))

  const activeRatesByRoom = useAppSelector(appState => getActivePropertyRatesByRoom(offerPageState, appState))
  const selectedRatesByRoom = useAppSelector(appState => getSelectedPropertyRatesByRoom(offerPageState, appState))

  const invalidSelectionByRoomId = useMemo(() => {
    const invalidByRoomId: Record<string, boolean> = {}

    rooms.forEach((room) => {
      if (!(room.roomId && room.rateId) || isRatesLoading) {
        invalidByRoomId[room.id] = false
        return
      }

      invalidByRoomId[room.id] = !selectedRatesByRoom[room.id]
    })

    return invalidByRoomId
  }, [isRatesLoading, rooms, selectedRatesByRoom])

  useEffect(() => {
    if (!isMultiRoomUIActive || isRatesLoading) {
      return
    }

    if (!propertyRatesKey) {
      // No dates selected: pre-select a default room type (room-only, no rate) for each slot, keeping
      // any already-valid room type. Mirrors the bedbank no-dates auto-assignment; with no per-room
      // price available before dates, the first room type (by display order) is the default.
      let didChangeAnyRoom = false
      const newRooms = rooms.map((room) => {
        const rows = rowsByRoomId[room.id] ?? EmptyArray
        if (rows.length === 0) {
          return room
        }
        const hasValidRoomType = !!room.roomId && rows.some(row => row.roomId === room.roomId)
        const nextRoomId = hasValidRoomType ? room.roomId : rows[0].roomId
        if (room.roomId === nextRoomId && !room.rateId) {
          return room
        }
        didChangeAnyRoom = true
        return { ...room, roomId: nextRoomId, rateId: undefined }
      })

      if (!didChangeAnyRoom) {
        hasResolvedOnceRef.current = true
        return
      }

      hasResolvedOnceRef.current = true
      dispatch({ type: OfferPageActions.setRooms, rooms: newRooms })
      if (multiRoomAutoselectionReason !== 'initial-selection') {
        dispatch({ type: OfferPageActions.setMultiRoomAutoselectionReason, reason: 'initial-selection' })
      }
      return
    }

    const hasMissingSelections = rooms.some(room => !(room.roomId && room.rateId))
    const hasUnavailableRoomSelections = rooms.some((room) => {
      if (!(room.roomId && !room.rateId)) {
        return false
      }
      const selection = activeRatesByRoom[room.id]
      return !selection || selection.roomId !== room.roomId
    })
    const hasInvalidSelections = rooms.some(room => !!invalidSelectionByRoomId[room.id]) || hasUnavailableRoomSelections

    if (!(hasMissingSelections || hasInvalidSelections)) {
      return
    }

    const isInitialResolve = !hasResolvedOnceRef.current
    const autoselectionReason: OfferPageMultiRoomAutoselectionReason = (isInitialResolve && hasInvalidSelections) ?
      'invalid-selections' :
      'initial-selection'

    let didChangeAnyRoom = false
    const newRooms = rooms.map((room) => {
      if ((room.roomId && room.rateId) && !invalidSelectionByRoomId[room.id]) {
        return room
      }
      const selection = activeRatesByRoom[room.id]
      const nextRoomId = selection?.roomId
      const nextRateId = selection?.optionId
      if (room.roomId === nextRoomId && room.rateId === nextRateId) {
        return room
      }
      didChangeAnyRoom = true
      return {
        ...room,
        roomId: nextRoomId,
        rateId: nextRateId,
      }
    })

    if (!didChangeAnyRoom) {
      hasResolvedOnceRef.current = true
      return
    }

    hasResolvedOnceRef.current = true
    dispatch({ type: OfferPageActions.setRooms, rooms: newRooms })
    if (multiRoomAutoselectionReason !== autoselectionReason) {
      dispatch({ type: OfferPageActions.setMultiRoomAutoselectionReason, reason: autoselectionReason })
    }
  }, [
    dispatch,
    isMultiRoomUIActive,
    rooms,
    activeRatesByRoom,
    invalidSelectionByRoomId,
    isRatesLoading,
    multiRoomAutoselectionReason,
    propertyRatesKey,
    rowsByRoomId,
  ])
}
